/**
 * Campaign Donation Page - Payment form for donations
 */
import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Checkbox } from '@/components/ui/checkbox';
import { Progress } from '@/components/ui/progress';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { getToken } from '@/lib/authToken';
import {
  Heart, ArrowLeft, CreditCard, Smartphone, Building2, 
  CheckCircle, Copy, Loader2, Banknote, Globe, Link as LinkIcon, AlertCircle
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PRESET_AMOUNTS = [25, 50, 100, 250, 500, 1000];

// Map payment-method id/type -> icon + fallback description
const PAYMENT_ICON_MAP = {
  cash: { icon: Banknote, description: 'Pay in cash' },
  bank_transfer: { icon: Building2, description: 'Direct bank deposit' },
  ecocash: { icon: Smartphone, description: 'Mobile money' },
  innbucks: { icon: Smartphone, description: 'Mobile money' },
  card: { icon: CreditCard, description: 'Credit/Debit card' },
  paypal: { icon: Globe, description: 'PayPal' },
  link: { icon: LinkIcon, description: 'Payment link' }
};
const DEFAULT_METHOD_META = { icon: CreditCard, description: 'Payment method' };

const CampaignDonate = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [donationResult, setDonationResult] = useState(null);
  
  const [formData, setFormData] = useState({
    amount: '',
    customAmount: '',
    currency: '',
    donor_name: '',
    donor_email: '',
    donor_phone: '',
    payment_method: '',
    message: '',
    is_anonymous: false,
    donor_visibility: 'anonymous'
  });

  // Detect preview mode from URL — admins viewing draft campaigns must be able
  // to load the donate page even before it's published. Mirrors CampaignLanding.
  const isPreview = React.useMemo(() => {
    try {
      return new URLSearchParams(window.location.search).get('preview') === 'true';
    } catch {
      return false;
    }
  }, []);

  // Fetch campaign — refetch on mount so that payment-method changes saved
  // by an admin on another tab show up the moment the donor reopens this page.
  const { data: campaign, isLoading } = useQuery({
    queryKey: ['campaign', slug, isPreview],
    queryFn: async () => {
      const url = isPreview
        ? `${API_URL}/api/campaigns/${slug}?preview=true`
        : `${API_URL}/api/campaigns/${slug}`;
      const res = await fetch(url);
      if (!res.ok) throw new Error('Campaign not found');
      return res.json();
    },
    staleTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
  });

  // Build enabled payment methods from the campaign (fallback to a minimal default)
  const enabledMethods = React.useMemo(() => {
    const list = (campaign?.payment_methods || []).filter(m => m && m.enabled);
    if (list.length > 0) return list;
    // Sensible fallback when the campaign has no configured methods
    return [
      { id: 'cash', name: 'Cash', enabled: true, type: 'offline' },
      { id: 'bank_transfer', name: 'Bank Transfer', enabled: true, type: 'offline' },
    ];
  }, [campaign]);

  // Donation CMS config — controls suggested amounts, donor instructions,
  // thank-you content, form-field visibility, default donor visibility, etc.
  // Falls back to safe defaults so the page works even when an admin never
  // touched the Donation CMS tab.
  const donationCms = React.useMemo(() => {
    const cms = campaign?.page_config?.donation_cms || {};
    const fields = cms.donor_form_fields || {};
    return {
      banner_video_url: cms.banner_video_url || '',
      donor_instructions: cms.donor_instructions || '',
      payment_instructions_html: cms.payment_instructions_html || '',
      thank_you_message: cms.thank_you_message || '',
      thank_you_video_url: cms.thank_you_video_url || '',
      default_donor_visibility: ['public', 'anonymous', 'private'].includes(cms.default_donor_visibility) ? cms.default_donor_visibility : 'public',
      suggested_amounts: Array.isArray(cms.suggested_amounts) && cms.suggested_amounts.length ? cms.suggested_amounts : null,
      giving_categories: Array.isArray(cms.giving_categories) ? cms.giving_categories : [],
      donor_form_fields: {
        show_phone: fields.show_phone !== false,
        phone_required: !!fields.phone_required,
        show_message: fields.show_message !== false,
        show_address: !!fields.show_address,
      },
    };
  }, [campaign]);

  // Apply the CMS default donor_visibility once when the campaign first loads.
  const cmsAppliedRef = React.useRef(false);
  React.useEffect(() => {
    if (!campaign || cmsAppliedRef.current) return;
    cmsAppliedRef.current = true;
    setFormData((prev) => ({
      ...prev,
      donor_visibility: donationCms.default_donor_visibility,
    }));
  }, [campaign, donationCms.default_donor_visibility]);

  // Ensure selected method is one of the enabled methods
  React.useEffect(() => {
    if (!enabledMethods.length) return;
    const ids = enabledMethods.map(m => m.id);
    if (!ids.includes(formData.payment_method)) {
      setFormData(prev => ({ ...prev, payment_method: enabledMethods[0].id }));
    }
  }, [enabledMethods]); // eslint-disable-line react-hooks/exhaustive-deps

  // Allowed currencies — when the admin restricts via campaign.allowed_currencies,
  // donors only see that list. Otherwise we offer the full ecosystem catalogue
  // so a donor can give in any currency (the FX engine converts to the
  // campaign's base currency on the backend).
  const [catalogueCurrencies, setCatalogueCurrencies] = React.useState([]);
  React.useEffect(() => {
    let cancelled = false;
    fetch(`${API_URL}/api/fx/catalogue`)
      .then(r => r.json())
      .then(d => {
        if (!cancelled && Array.isArray(d?.items)) {
          setCatalogueCurrencies(d.items.map(i => i.code));
        }
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const allowedCurrencies = React.useMemo(() => {
    // The campaigns API back-fills allowed_currencies with [base] when admin
    // didn't restrict — treat that case as "any catalogue currency" so donors
    // can still pay in their local currency.
    const raw = (campaign?.allowed_currencies || []).filter(Boolean);
    const baseCcy = (campaign?.currency || 'USD').toUpperCase();
    const adminRestricted = raw.length > 1 ||
      (raw.length === 1 && raw[0].toUpperCase() !== baseCcy);
    if (adminRestricted) return raw.map(c => c.toUpperCase());
    if (catalogueCurrencies.length > 0) {
      // Put base currency first for a clean default
      return [baseCcy, ...catalogueCurrencies.filter(c => c !== baseCcy)];
    }
    return [baseCcy];
  }, [campaign, catalogueCurrencies]);
  // Auto-detect the donor's currency once, when the campaign first loads. We
  // only override the default when the detected currency is one of the
  // campaign's allowed currencies — never silently switch to something the
  // campaign doesn't accept.
  const [autoCurrencyApplied, setAutoCurrencyApplied] = React.useState(false);
  React.useEffect(() => {
    if (autoCurrencyApplied || !campaign) return;
    if (!allowedCurrencies.length || allowedCurrencies.length === 1) {
      setAutoCurrencyApplied(true);
      return;
    }
    fetch(`${API_URL}/api/fx/detect-currency?allowed=${allowedCurrencies.join(',')}`)
      .then(r => r.json())
      .then(d => {
        if (d && d.currency && allowedCurrencies.includes(d.currency)) {
          setFormData(prev => ({ ...prev, currency: d.currency }));
        }
      })
      .catch(() => {})
      .finally(() => setAutoCurrencyApplied(true));
  }, [campaign, allowedCurrencies, autoCurrencyApplied]);
  // Ensure the selected currency is in the allowed list once campaign loads
  React.useEffect(() => {
    if (!allowedCurrencies.length) return;
    if (!allowedCurrencies.includes(formData.currency)) {
      setFormData(prev => ({ ...prev, currency: allowedCurrencies[0] }));
    }
  }, [allowedCurrencies]); // eslint-disable-line react-hooks/exhaustive-deps

  const currencySymbol = React.useMemo(() => {
    const map = { USD: '$', EUR: '€', GBP: '£', ZAR: 'R', ZWL: 'Z$', ZWG: 'ZWG ', NGN: '₦', KES: 'KSh', GHS: 'GH₵', INR: '₹' };
    return map[formData.currency] || `${formData.currency} `;
  }, [formData.currency]);

  // Live FX preview — "≈ USD 116.01 (@ 1.1601)" when donor selects non-base currency
  const [fxPreview, setFxPreview] = React.useState(null);
  // Localized suggested-amount tiers — when donor flips to a non-base currency
  // we ask /api/fx/suggested-amounts to convert and smart-round the campaign's
  // preset ladder so buttons feel native (£10/£25/£50… instead of £21.71/£43.42…).
  // The base ladder is the CMS-configured one when present, else PRESET_AMOUNTS.
  const baseAmounts = donationCms.suggested_amounts || PRESET_AMOUNTS;
  const [localizedAmounts, setLocalizedAmounts] = React.useState(null);
  React.useEffect(() => {
    const baseCcy = (campaign?.currency || 'USD').toUpperCase();
    const donorCcy = (formData.currency || baseCcy).toUpperCase();
    if (donorCcy === baseCcy) { setLocalizedAmounts(null); return; }
    let cancelled = false;
    const url = `${API_URL}/api/fx/suggested-amounts?amounts=${baseAmounts.join(',')}&from=${baseCcy}&to=${donorCcy}`;
    fetch(url).then(r => r.json()).then(d => {
      if (!cancelled && d && Array.isArray(d.items) && d.items.length) {
        setLocalizedAmounts(d.items.map(it => it.rounded));
      }
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [formData.currency, campaign?.currency, baseAmounts]);

  const displayAmounts = localizedAmounts || baseAmounts;

  React.useEffect(() => {
    const baseCcy = (campaign?.currency || 'USD').toUpperCase();
    const donorCcy = (formData.currency || baseCcy).toUpperCase();
    const amt = parseFloat(formData.amount || formData.customAmount || 0);
    if (!amt || donorCcy === baseCcy) { setFxPreview(null); return; }
    let cancelled = false;
    fetch(`${API_URL}/api/fx/convert`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ amount: amt, from_currency: donorCcy, to_currency: baseCcy }),
    }).then(r => r.json()).then(d => {
      if (!cancelled && d && d.rate > 0) {
        setFxPreview({ base_ccy: baseCcy, base_amount: d.converted_amount, rate: d.rate });
      }
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [formData.amount, formData.customAmount, formData.currency, campaign?.currency]);

  const finalAmount = formData.customAmount || formData.amount;

  const handleAmountSelect = (amount) => {
    setFormData({ ...formData, amount: amount.toString(), customAmount: '' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!finalAmount || parseFloat(finalAmount) <= 0) {
      toast.error('Please enter a valid donation amount');
      return;
    }
    
    if (!formData.donor_name || !formData.donor_email) {
      toast.error('Please fill in your name and email');
      return;
    }

    setSubmitting(true);

    // ============== STRIPE PATH ==============
    // If the donor picked "card" (Stripe), create a Stripe Checkout session
    // and redirect there instead of just recording a pledge. The session
    // creator pre-inserts a pending campaign_donations row so admin sees the
    // pledge immediately; the webhook flips status -> completed on payment.
    if (formData.payment_method === 'card') {
      try {
        const res = await fetch(`${API_URL}/api/stripe/checkout/campaign-donation`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            slug,
            donor_name: formData.donor_name,
            donor_email: formData.donor_email,
            donor_phone: formData.donor_phone || null,
            amount: parseFloat(finalAmount),
            currency: formData.currency || 'USD',
            message: formData.message || null,
            donor_visibility: formData.donor_visibility,
            is_anonymous: formData.is_anonymous,
            origin_url: window.location.origin,
          }),
        });
        const result = await res.json();
        if (res.ok && result.url) {
          // Redirect to Stripe Checkout — donor returns to /payment/success
          window.location.href = result.url;
          return;
        }
        toast.error(result.detail || 'Failed to start card payment');
      } catch (error) {
        toast.error('Failed to start card payment');
      }
      setSubmitting(false);
      return;
    }

    // ============== OFFLINE / PLEDGE PATH ==============
    // A pledge is only recorded against a signed-in identity. Anonymous givers
    // pay by card, where the gateway confirms the money.
    const token = getToken();
    if (!token) {
      toast.error('Sign in to record an offline pledge, or choose Card to pay now');
      setSubmitting(false);
      return;
    }
    try {
      const submitData = new FormData();
      submitData.append('amount', finalAmount);
      submitData.append('currency', formData.currency || campaign?.currency || 'USD');
      submitData.append('donor_name', formData.donor_name);
      submitData.append('donor_email', formData.donor_email);
      submitData.append('donor_phone', formData.donor_phone || '');
      submitData.append('payment_method', formData.payment_method);
      submitData.append('message', formData.message || '');
      submitData.append('is_anonymous', formData.is_anonymous.toString());
      submitData.append('donor_visibility', formData.donor_visibility);

      const res = await fetch(`${API_URL}/api/campaigns/${slug}/donate`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: submitData
      });

      const result = await res.json();

      if (res.ok) {
        // ============== LINK AUTO-REDIRECT ==============
        // When the donor picks the "link" payment method and the campaign has a
        // configured payment_url, send them straight to the processor (PayNow,
        // Mukuru, etc.) instead of making them tap an extra "Pay Now" button on
        // step 3. The processor will return them via the embedded return_url.
        if (formData.payment_method === 'link') {
          const linkMethod = (campaign?.payment_methods || []).find(
            (m) => m && m.id === 'link' && m.enabled,
          );
          const payUrl =
            (result.payment_method_details && result.payment_method_details.payment_url) ||
            (linkMethod && linkMethod.payment_url);
          if (payUrl) {
            const returnUrl = `${window.location.origin}/campaigns/${slug}`;
            const sep = payUrl.includes('?') ? '&' : '?';
            const fullUrl =
              `${payUrl}${sep}return_url=${encodeURIComponent(returnUrl)}` +
              `&ref=${encodeURIComponent(result.reference || `CAMPAIGN-${slug}`)}`;
            toast.success('Redirecting to payment provider…');
            window.location.href = fullUrl;
            return;
          }
        }

        setDonationResult(result);
        setStep(3);
        toast.success('Donation recorded! Follow payment instructions below.');
      } else {
        toast.error(result.detail || 'Failed to process donation');
      }
    } catch (error) {
      toast.error('Something went wrong');
    }
    
    setSubmitting(false);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard!');
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-50 py-12">
        <div className="max-w-2xl mx-auto px-4">
          <Skeleton className="h-64 w-full rounded-xl" />
        </div>
      </div>
    );
  }

  if (!campaign) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Card className="p-8 text-center">
          <h2 className="text-xl font-semibold mb-2">Campaign Not Found</h2>
          <Link to="/campaigns"><Button>View All Campaigns</Button></Link>
        </Card>
      </div>
    );
  }

  const progressPercent = campaign.target_amount > 0 
    ? Math.min(100, Math.round((campaign.raised_amount / campaign.target_amount) * 100))
    : 0;

  return (
    <div className="min-h-screen bg-gradient-to-b from-emerald-50 to-white py-8">
      {isPreview && (
        <div
          className="max-w-2xl mx-auto px-4 mb-4"
          data-testid="donate-preview-banner"
        >
          <div className="bg-amber-50 border border-amber-300 text-amber-900 rounded-md px-4 py-2 text-sm flex items-start gap-2">
            <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
            <div>
              <strong>Preview Mode</strong> — this campaign is not published yet.
              Only admins with the link can see this page.
            </div>
          </div>
        </div>
      )}
      <div className="max-w-2xl mx-auto px-4">
        {/* Header */}
        <div className="mb-6">
          <Link 
            to={`/campaigns/${slug}${isPreview ? '?preview=true' : ''}`}
            className="inline-flex items-center text-sm text-slate-600 hover:text-slate-900 mb-4"
          >
            <ArrowLeft className="h-4 w-4 mr-1" />
            Back to Campaign
          </Link>
          <h1 className="text-2xl font-bold text-slate-900">{campaign.name}</h1>
          <p className="text-slate-500">Support this project with a donation</p>
        </div>

        {/* Progress Summary */}
        <Card className="mb-6">
          <CardContent className="py-4">
            <div className="flex justify-between text-sm mb-2">
              <span className="font-semibold text-emerald-600">
                {campaign.currency || 'USD'} {campaign.raised_amount?.toLocaleString() || 0} raised
              </span>
              <span className="text-slate-500">
                of {campaign.currency || 'USD'} {campaign.target_amount?.toLocaleString() || 0} goal
              </span>
            </div>
            <Progress value={progressPercent} className="h-2" />
            {campaign.raised_by_currency && Object.keys(campaign.raised_by_currency).length > 1 && (
              <div className="mt-3 pt-3 border-t flex flex-wrap gap-2" data-testid="donate-per-currency-totals">
                {Object.entries(campaign.raised_by_currency).map(([cur, amt]) => (
                  <span key={cur} className="inline-flex items-center gap-1 text-xs bg-emerald-50 text-emerald-700 px-2 py-1 rounded">
                    <strong>{cur}</strong> {Number(amt).toLocaleString()}
                  </span>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Step Indicator */}
        <div className="flex items-center justify-center gap-2 mb-8">
          {[1, 2, 3].map((s) => (
            <div 
              key={s}
              className={`h-2 w-16 rounded-full transition-colors ${
                s <= step ? 'bg-emerald-500' : 'bg-slate-200'
              }`}
            />
          ))}
        </div>

        {/* Step 1: Amount Selection */}
        {step === 1 && (
          <>
            {donationCms.donor_instructions && (
              <Card className="mb-4 border-emerald-100 bg-emerald-50/30" data-testid="donate-donor-instructions">
                <CardContent className="pt-4 prose prose-sm max-w-none">
                  <div dangerouslySetInnerHTML={{ __html: donationCms.donor_instructions }} />
                </CardContent>
              </Card>
            )}
            {donationCms.banner_video_url && (
              <Card className="mb-4 overflow-hidden" data-testid="donate-banner-video">
                <div className="aspect-video bg-black">
                  {/youtube\.com|youtu\.be/.test(donationCms.banner_video_url) ? (
                    <iframe
                      title="Campaign banner video"
                      src={donationCms.banner_video_url.replace('watch?v=', 'embed/').replace('youtu.be/', 'youtube.com/embed/')}
                      className="w-full h-full" allowFullScreen
                    />
                  ) : (
                    <video src={donationCms.banner_video_url} controls className="w-full h-full" />
                  )}
                </div>
              </Card>
            )}
          <Card>
            <CardHeader>
              <CardTitle>Choose Amount</CardTitle>
              <CardDescription>Select a preset amount or enter your own</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {allowedCurrencies.length > 1 && (
                <div data-testid="donate-currency-selector">
                  <Label className="text-xs uppercase tracking-wide text-slate-500 font-semibold mb-1 block">
                    Give in your currency
                  </Label>
                  <p className="text-xs text-slate-500 mb-2">
                    Pick any currency you like — we&apos;ll convert it to{' '}
                    <strong className="text-emerald-700">{(campaign?.currency || 'USD').toUpperCase()}</strong>{' '}
                    (the campaign&apos;s currency) at the live FX rate.
                  </p>
                  {allowedCurrencies.length <= 8 ? (
                    <div className="flex flex-wrap gap-2">
                      {allowedCurrencies.map(c => (
                        <button
                          key={c} type="button"
                          onClick={() => setFormData({ ...formData, currency: c })}
                          className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition ${
                            formData.currency === c
                              ? 'bg-emerald-600 text-white border-emerald-600'
                              : 'bg-white text-slate-700 border-slate-200 hover:border-emerald-300'
                          }`}
                          data-testid={`donate-currency-${c}`}
                        >
                          {c}
                        </button>
                      ))}
                    </div>
                  ) : (
                    <select
                      value={formData.currency}
                      onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
                      className="w-full h-10 border rounded-md px-3 text-sm bg-white"
                      data-testid="donate-currency-select"
                    >
                      {allowedCurrencies.map(c => (
                        <option key={c} value={c} data-testid={`donate-currency-${c}`}>{c}</option>
                      ))}
                    </select>
                  )}
                </div>
              )}
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {displayAmounts.map((amount) => (
                  <Button
                    key={amount}
                    type="button"
                    variant={formData.amount === amount.toString() ? 'default' : 'outline'}
                    className={formData.amount === amount.toString() ? 'bg-emerald-600 hover:bg-emerald-700' : ''}
                    onClick={() => handleAmountSelect(amount)}
                    data-testid={`donate-preset-${amount}`}
                  >
                    {currencySymbol}{amount}
                  </Button>
                ))}
              </div>

              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">{currencySymbol}</span>
                <Input
                  type="number"
                  placeholder="Enter custom amount"
                  value={formData.customAmount}
                  onChange={(e) => setFormData({ ...formData, customAmount: e.target.value, amount: '' })}
                  className="pl-10 text-lg"
                  data-testid="donate-custom-amount"
                />
              </div>

              {fxPreview && (
                <div
                  className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200"
                  data-testid="donate-fx-preview"
                >
                  ≈ <strong>{fxPreview.base_ccy} {Number(fxPreview.base_amount).toFixed(2)}</strong>
                  <span className="text-emerald-500">· rate {fxPreview.rate.toFixed(4)}</span>
                </div>
              )}

              <Button 
                onClick={() => setStep(2)}
                disabled={!finalAmount || parseFloat(finalAmount) <= 0}
                className="w-full bg-emerald-600 hover:bg-emerald-700"
                size="lg"
              >
                Continue with {currencySymbol}{finalAmount || 0}
              </Button>
            </CardContent>
          </Card>
          </>
        )}

        {/* Step 2: Details & Payment */}
        {step === 2 && (
          <form onSubmit={handleSubmit}>
            <Card className="mb-4">
              <CardHeader>
                <CardTitle>Your Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Donor Visibility */}
                <div>
                  <Label className="text-sm font-medium mb-2 block">How would you like to appear publicly?</Label>
                  <div className="grid grid-cols-2 gap-2">
                    {[
                      { v: 'public', l: 'Public', desc: 'Show my full name' },
                      { v: 'partial', l: 'Partial', desc: 'First name + initial' },
                      { v: 'anonymous', l: 'Anonymous', desc: 'Show as "Anonymous"' },
                      { v: 'private', l: 'Private', desc: 'Don\'t show donation publicly' },
                    ].map(opt => (
                      <button key={opt.v} type="button" onClick={() => setFormData({ ...formData, donor_visibility: opt.v, is_anonymous: opt.v === 'anonymous' || opt.v === 'private' })}
                        className={`p-2.5 rounded-lg border text-left transition-all ${
                          formData.donor_visibility === opt.v 
                            ? 'bg-emerald-50 border-emerald-400 ring-1 ring-emerald-400' 
                            : 'bg-white border-slate-200 hover:border-slate-300'
                        }`}
                        data-testid={`campaign-visibility-${opt.v}`}
                      >
                        <p className={`text-sm font-medium ${formData.donor_visibility === opt.v ? 'text-emerald-700' : 'text-slate-700'}`}>{opt.l}</p>
                        <p className="text-xs text-slate-500">{opt.desc}</p>
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <Label htmlFor="donor_name">Full Name * <span className="text-xs text-slate-400">{formData.donor_visibility === 'anonymous' || formData.donor_visibility === 'private' ? '(admin only, not shown publicly)' : ''}</span></Label>
                  <Input
                    id="donor_name"
                    value={formData.donor_name}
                    onChange={(e) => setFormData({ ...formData, donor_name: e.target.value })}
                    placeholder="John Doe"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="donor_email">Email * <span className="text-xs text-slate-400">(for receipt & updates)</span></Label>
                  <Input
                    id="donor_email"
                    type="email"
                    value={formData.donor_email}
                    onChange={(e) => setFormData({ ...formData, donor_email: e.target.value })}
                    placeholder="john@example.com"
                    required
                  />
                </div>
                {donationCms.donor_form_fields.show_phone && (
                  <div>
                    <Label htmlFor="donor_phone">
                      Phone{donationCms.donor_form_fields.phone_required && ' *'}{' '}
                      <span className="text-xs text-slate-400">(for contact & updates)</span>
                    </Label>
                    <Input
                      id="donor_phone"
                      value={formData.donor_phone}
                      onChange={(e) => setFormData({ ...formData, donor_phone: e.target.value })}
                      placeholder="+263 77 123 4567"
                      required={donationCms.donor_form_fields.phone_required}
                      data-testid="donate-phone-input"
                    />
                  </div>
                )}
                {donationCms.donor_form_fields.show_message && (
                  <div>
                    <Label htmlFor="message">Message (optional) <span className="text-xs text-slate-400">— shown publicly even if anonymous</span></Label>
                    <Textarea
                      id="message"
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      placeholder="Leave a message of support..."
                      rows={2}
                      data-testid="donate-message-input"
                    />
                  </div>
                )}
              </CardContent>
            </Card>

            <Card className="mb-4">
              <CardHeader>
                <CardTitle>Payment Method</CardTitle>
              </CardHeader>
              <CardContent>
                {donationCms.payment_instructions_html && (
                  <div
                    className="text-sm prose prose-sm max-w-none mb-3 p-3 rounded-md bg-slate-50 border border-slate-200"
                    dangerouslySetInnerHTML={{ __html: donationCms.payment_instructions_html }}
                    data-testid="donate-cms-payment-instructions"
                  />
                )}
                <RadioGroup 
                  value={formData.payment_method}
                  onValueChange={(value) => setFormData({ ...formData, payment_method: value })}
                  className="space-y-3"
                >
                  {enabledMethods.map((method) => {
                    const meta = PAYMENT_ICON_MAP[method.id] || DEFAULT_METHOD_META;
                    const Icon = meta.icon;
                    const description = method.instructions || meta.description;
                    const returnUrl = typeof window !== 'undefined' ? window.location.href : '';
                    const paymentUrlWithReturn = method.payment_url
                      ? `${method.payment_url}${method.payment_url.includes('?') ? '&' : '?'}return_url=${encodeURIComponent(returnUrl)}&ref=${encodeURIComponent(`CAMPAIGN-${slug}`)}`
                      : null;
                    return (
                      <label
                        key={method.id}
                        className={`flex items-start gap-4 p-4 border rounded-lg cursor-pointer transition-colors ${
                          formData.payment_method === method.id ? 'border-emerald-500 bg-emerald-50' : 'hover:bg-slate-50'
                        }`}
                        data-testid={`donate-payment-method-${method.id}`}
                      >
                        <RadioGroupItem value={method.id} className="mt-1" />
                        <Icon className="h-6 w-6 text-slate-600 mt-0.5" />
                        <div className="flex-1 min-w-0">
                          <p className="font-medium">{method.name || method.id}</p>
                          {description && <p className="text-sm text-slate-500">{description}</p>}

                          {/* EcoCash / InnBucks — show merchant code inline */}
                          {(method.id === 'ecocash' || method.id === 'innbucks') && method.merchant_code && (
                            <div className="mt-2 flex flex-wrap items-center gap-2 text-sm">
                              <span className="text-slate-500">Merchant Code:</span>
                              <code
                                className="px-2 py-0.5 bg-white border border-slate-200 rounded font-mono font-bold text-emerald-700"
                                data-testid={`donate-merchant-code-${method.id}`}
                              >{method.merchant_code}</code>
                              <button
                                type="button"
                                onClick={(e) => { e.preventDefault(); copyToClipboard(method.merchant_code); }}
                                className="text-xs text-emerald-600 hover:underline"
                              >Copy</button>
                              {method.merchant_name && (
                                <span className="text-xs text-slate-500">({method.merchant_name})</span>
                              )}
                            </div>
                          )}

                          {/* Payment Link — clickable button that opens in a new tab and asks the processor to redirect back */}
                          {method.id === 'link' && paymentUrlWithReturn && (
                            <a
                              href={paymentUrlWithReturn}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={(e) => e.stopPropagation()}
                              className="mt-2 inline-flex items-center gap-1.5 text-sm text-emerald-700 hover:text-emerald-800 underline font-medium"
                              data-testid="donate-payment-link"
                            >
                              Open Payment Link
                              <LinkIcon className="h-3.5 w-3.5" />
                            </a>
                          )}

                          {/* Bank transfer — show brief details inline */}
                          {method.id === 'bank_transfer' && (method.bank_name || method.account_number) && (
                            <div className="mt-2 text-xs text-slate-600 space-y-0.5">
                              {method.bank_name && <div><span className="text-slate-500">Bank:</span> {method.bank_name}</div>}
                              {method.account_name && <div><span className="text-slate-500">Account Name:</span> {method.account_name}</div>}
                              {method.account_number && <div><span className="text-slate-500">Account #:</span> <code className="font-mono">{method.account_number}</code></div>}
                            </div>
                          )}
                        </div>
                      </label>
                    );
                  })}
                </RadioGroup>
              </CardContent>
            </Card>

            <div className="flex gap-3">
              <Button type="button" variant="outline" onClick={() => setStep(1)} className="flex-1">
                Back
              </Button>
              <Button 
                type="submit" 
                disabled={submitting}
                className="flex-1 bg-emerald-600 hover:bg-emerald-700"
              >
                {submitting ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    {formData.payment_method === 'card' ? 'Redirecting to Stripe…'
                      : formData.payment_method === 'link' ? 'Redirecting to payment link…'
                      : 'Processing...'}
                  </>
                ) : (
                  <>
                    {formData.payment_method === 'card' ? (
                      <>
                        <CreditCard className="h-4 w-4 mr-2" />
                        Pay ${finalAmount} with Card
                      </>
                    ) : formData.payment_method === 'link' ? (
                      <>
                        <LinkIcon className="h-4 w-4 mr-2" />
                        Pay ${finalAmount} via Link
                      </>
                    ) : (
                      <>
                        <Heart className="h-4 w-4 mr-2" />
                        Donate ${finalAmount}
                      </>
                    )}
                  </>
                )}
              </Button>
            </div>
          </form>
        )}

        {/* Step 3: Confirmation & Payment Instructions */}
        {step === 3 && donationResult && (
          <Card>
            <CardContent className="py-8 text-center">
              <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="h-8 w-8 text-emerald-600" />
              </div>
              <h2 className="text-2xl font-bold mb-2">Thank You!</h2>
              <p className="text-slate-600 mb-6">
                Your donation of ${finalAmount} has been recorded.
              </p>

              {donationCms.thank_you_message && (
                <div
                  className="prose prose-sm max-w-none text-left mb-6 px-2"
                  dangerouslySetInnerHTML={{ __html: donationCms.thank_you_message }}
                  data-testid="donate-cms-thank-you-message"
                />
              )}
              {donationCms.thank_you_video_url && (
                <div className="aspect-video mb-6 rounded-lg overflow-hidden bg-black" data-testid="donate-cms-thank-you-video">
                  {/youtube\.com|youtu\.be/.test(donationCms.thank_you_video_url) ? (
                    <iframe
                      title="Thank-you video"
                      src={donationCms.thank_you_video_url.replace('watch?v=', 'embed/').replace('youtu.be/', 'youtube.com/embed/')}
                      className="w-full h-full" allowFullScreen
                    />
                  ) : (
                    <video src={donationCms.thank_you_video_url} controls className="w-full h-full" />
                  )}
                </div>
              )}

              {/* Reference Number */}
              <div className="bg-slate-100 rounded-lg p-4 mb-6">
                <p className="text-sm text-slate-500 mb-1">Reference Number</p>
                <div className="flex items-center justify-center gap-2">
                  <span className="text-xl font-mono font-bold">{donationResult.reference}</span>
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => copyToClipboard(donationResult.reference)}
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              {/* Rich Payment Details from campaign config (merchant code / payment link / bank) */}
              {donationResult.payment_method_details && (() => {
                const md = donationResult.payment_method_details;
                const mid = donationResult.payment_method;
                const returnUrl = typeof window !== 'undefined' ? window.location.origin + `/campaigns/${slug}` : '';
                const linkUrl = md.payment_url
                  ? `${md.payment_url}${md.payment_url.includes('?') ? '&' : '?'}return_url=${encodeURIComponent(returnUrl)}&ref=${encodeURIComponent(donationResult.reference)}`
                  : null;
                return (
                  <div className="text-left bg-emerald-50 border border-emerald-200 rounded-lg p-4 mb-4" data-testid="donate-method-details">
                    <h3 className="font-semibold text-emerald-800 mb-3">How to Pay — {md.name || mid}</h3>

                    {(mid === 'ecocash' || mid === 'innbucks') && md.merchant_code && (
                      <div className="space-y-2 text-sm">
                        <p className="text-emerald-900">
                          Send <strong>${finalAmount}</strong> via {md.name || 'mobile money'} to merchant code:
                        </p>
                        <div className="flex items-center gap-2 bg-white border border-emerald-200 rounded-md px-3 py-2">
                          <code className="flex-1 font-mono font-bold text-lg text-emerald-700" data-testid="donate-confirm-merchant-code">{md.merchant_code}</code>
                          <Button type="button" variant="ghost" size="sm" onClick={() => copyToClipboard(md.merchant_code)}>
                            <Copy className="h-4 w-4" />
                          </Button>
                        </div>
                        {md.merchant_name && <p className="text-xs text-slate-600">Merchant: <strong>{md.merchant_name}</strong></p>}
                        <p className="text-xs text-slate-600">Use reference <code className="font-mono">{donationResult.reference}</code> so we can match your payment.</p>
                      </div>
                    )}

                    {mid === 'link' && linkUrl && (
                      <div className="space-y-3">
                        <p className="text-sm text-emerald-900">Tap the button below to complete your payment securely. You'll be brought back here when you're done.</p>
                        <a
                          href={linkUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center justify-center gap-2 w-full px-4 py-3 bg-emerald-600 hover:bg-emerald-700 text-white font-medium rounded-lg transition-colors"
                          data-testid="donate-confirm-payment-link"
                        >
                          Pay ${finalAmount} Now
                          <LinkIcon className="h-4 w-4" />
                        </a>
                        <p className="text-xs text-slate-500 text-center">After payment, you'll be returned to this campaign page.</p>
                      </div>
                    )}

                    {mid === 'bank_transfer' && (md.bank_name || md.account_number) && (
                      <div className="space-y-1.5 text-sm text-emerald-900">
                        {md.bank_name && <p><strong>Bank:</strong> {md.bank_name}</p>}
                        {md.account_name && <p><strong>Account Name:</strong> {md.account_name}</p>}
                        {md.account_number && <p><strong>Account #:</strong> <code className="font-mono">{md.account_number}</code></p>}
                        {md.branch_code && <p><strong>Branch:</strong> {md.branch_code}</p>}
                        {md.swift_code && <p><strong>SWIFT:</strong> {md.swift_code}</p>}
                        <p className="pt-1"><strong>Reference:</strong> <code className="font-mono">{donationResult.reference}</code></p>
                      </div>
                    )}

                    {mid === 'paypal' && md.paypal_email && (
                      <div className="space-y-2 text-sm text-emerald-900">
                        <p>Send <strong>${finalAmount}</strong> via PayPal to:</p>
                        <div className="flex items-center gap-2 bg-white border border-emerald-200 rounded-md px-3 py-2">
                          <code className="flex-1 font-mono">{md.paypal_email}</code>
                          <Button type="button" variant="ghost" size="sm" onClick={() => copyToClipboard(md.paypal_email)}>
                            <Copy className="h-4 w-4" />
                          </Button>
                        </div>
                        <p className="text-xs text-slate-600">Use reference <code className="font-mono">{donationResult.reference}</code></p>
                      </div>
                    )}

                    {md.instructions && (
                      <p className="mt-3 text-xs text-slate-600 border-t border-emerald-200 pt-2">{md.instructions}</p>
                    )}
                  </div>
                );
              })()}

              {/* Fallback: Payment Instructions from generic template */}
              {donationResult.payment_instructions && !donationResult.payment_method_details && (
                <div className="text-left bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
                  <h3 className="font-semibold text-amber-800 mb-3">Payment Instructions</h3>
                  {donationResult.payment_instructions.steps ? (
                    <ol className="space-y-2">
                      {donationResult.payment_instructions.steps.map((step, i) => (
                        <li key={i} className="flex gap-2 text-sm text-amber-900">
                          <span className="font-medium">{i + 1}.</span>
                          {step}
                        </li>
                      ))}
                    </ol>
                  ) : (
                    <div className="space-y-2 text-sm text-amber-900">
                      <p><strong>Bank:</strong> {donationResult.payment_instructions.bank_name}</p>
                      <p><strong>Account Name:</strong> {donationResult.payment_instructions.account_name}</p>
                      <p><strong>Account Number:</strong> {donationResult.payment_instructions.account_number}</p>
                      <p><strong>Reference:</strong> {donationResult.payment_instructions.reference}</p>
                    </div>
                  )}
                </div>
              )}

              <div className="flex gap-3">
                <Link to={`/campaigns/${slug}`} className="flex-1">
                  <Button variant="outline" className="w-full">
                    Back to Campaign
                  </Button>
                </Link>
                <Button 
                  onClick={() => {
                    setStep(1);
                    setFormData({
                      amount: '', customAmount: '', donor_name: '', donor_email: '',
                      donor_phone: '', payment_method: enabledMethods[0]?.id || '', message: '', is_anonymous: false,
                      donor_visibility: 'anonymous'
                    });
                    setDonationResult(null);
                  }}
                  className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                >
                  Make Another Donation
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default CampaignDonate;
